import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { debounce } from "lodash";
import { UserContext } from "../../../../../App";
import { useDispatch } from "react-redux";
import {
  orderFilter,
  searchTextRes,
  searchTextRespaymnet,
  setFocus,
  setLoading,
} from "../../../product/all-products/redux/Actions/paginationActions";
import {
// customerSearch,
  customerSearchV2Api,
  getGlobalProductsList,
} from "../../../../../config/Services";
import { customEventTrigger } from "../../../../../analytics";
import "./commonSearchbar.css";
import PosCustomIcon from "../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";

const CommonSearchBar = (props) => {
  const {
    searchText,
    setSearchText,
    categoryName,
    setCategoryName,
    setparticularCategoryItems,
    color,
    setSearchActive,
    saleMode,
    firstTimeSearchRef,
    hasMore,
    setHasMore,
    page,
    setPage,
  } = useContext(UserContext);

  const dispatchSearch = useDispatch();
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const { pathname } = useLocation();
  const showSnackbar = useSnackbar();
  const retailType = localStorage.getItem("retailType");

  const [debouncedText, setDebouncedText] = useState(searchText);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const prevSearchTextRef = useRef(searchText);
  const hasMountedRef = useRef(false);

  // ---------- Debounce Setup ----------
  const updateDebounced = useMemo(
    () =>
      debounce((value) => {
        setDebouncedText(value);
      }, 300),
    []
  );

  useEffect(() => {
    // Detect manual clearing: previous had text, now empty, AND input is focused (user is actively editing)
    const prevHadText = prevSearchTextRef.current && prevSearchTextRef.current.trim();
    const nowEmpty = !searchText || !searchText.trim();

    if (prevHadText && nowEmpty && isInputFocused) {
      // User manually cleared the search with backspace, set flag to trigger API refresh
      firstTimeSearchRef.current = true;
    }

    prevSearchTextRef.current = searchText;
    updateDebounced(searchText);
    return () => updateDebounced.cancel();
  }, [searchText, isInputFocused]);

  const fetchCategoryData = () => {
    const payload = {
      page: 1,
      pageSize: 20,
      ...(retailType !== "RESTAURANT" && { flattenResponse: true }),
      storeLocations: [locId],
      storeIds: [orgId],
      ...(retailType === "RESTAURANT" && { productTypes: ["MENU"] }),
      ...(retailType === "RESTAURANT" && { salesChannels: [saleMode] }),
      ...(retailType === "RESTAURANT" && { stockStatuses: ["IN_STOCK"] }),
      ...(retailType === "RESTAURANT" && { bundleIncluded: true }),
    };

    setparticularCategoryItems([]);
    return getGlobalProductsList(payload)
      .then((res) => {
        const cmsResult = res?.data?.data?.data?.data || [];
        setparticularCategoryItems(cmsResult);
        setSearchActive(false);
        setHasMore(
          res?.data?.data?.data?.currentPage <
            res?.data?.data?.data?.totalPages
        );
        setPage(1);
      })
      .catch((err) => {
        showSnackbar(
          err?.message || err || "Failed to fetch products",
          "error"
        );
      });
  };

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (debouncedText === null) return;

    const numArr = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
    const isNumberSearch = numArr.includes(Number(debouncedText?.[0]));

    dispatchSearch(searchTextRes(debouncedText));
    dispatchSearch(searchTextRespaymnet(debouncedText));

    // Case 1: When cleared
    if (!debouncedText.trim()) {
      if (firstTimeSearchRef.current) {
        if (props?.setSelectedCategory) props.setSelectedCategory("");
        if (props?.handleFilters) props.handleFilters("ALL", "");
        setCategoryName("");

        if (props?.queryManagedProducts) {
          dispatchSearch(setLoading(false));
        } else {
          dispatchSearch(setLoading(true));
          fetchCategoryData().finally(() => {
            dispatchSearch(setLoading(false));
          });
        }

        firstTimeSearchRef.current = false;
      } else {
        dispatchSearch(setLoading(false));
      }

      setSearchActive(false);
      return;
    }

    // Case 2: When user types search
    setSearchActive(true);
    dispatchSearch(setLoading(true));

    if (categoryName || props?.activeFilter !== "ALL") {
      props?.setSelectedCategory?.("");
      props?.handleFilters?.("ALL", "", { preserveSearch: true, skipFetch: true });
      setCategoryName("");
    }

    if (
      pathname === "/products/all-products" ||
      pathname === "/restaurant/cart" ||
      pathname === "/grocery/cart"
    ) {
      if (!firstTimeSearchRef.current) firstTimeSearchRef.current = true;

      setPage(1);
      const suggestionPayload = {
        page: 1,
        pageSize: 20,
        query: debouncedText,
        storeLocations: [locId],
        storeIds: [orgId],
        ...(retailType !== "RESTAURANT" && { flattenResponse: true }),
        ...(retailType === "RESTAURANT" && { productTypes: ["MENU"] }),
        ...(retailType === "RESTAURANT" && { salesChannels: [saleMode] }),
        ...(retailType === "RESTAURANT" && { stockStatuses: ["IN_STOCK"] }),
        ...(retailType === "RESTAURANT" && { bundleIncluded: true }),
      };

      customEventTrigger("pos_particular_product_page", {
        search_term: debouncedText,
      });

      getGlobalProductsList(suggestionPayload)
        .then((res) => {
          setSearchActive(true);
          if (res?.data?.data?.data?.totalRecords === "0") {
            setparticularCategoryItems([]);
            showSnackbar("No results found!", "error");
          } else {
            setparticularCategoryItems(res?.data?.data?.data?.data);
          }

          setHasMore(
            res?.data?.data?.data?.currentPage <
              res?.data?.data?.data?.totalPages
          );
        })
        .catch((err) => {
          showSnackbar(err?.response?.data?.message || "Search failed", "error");
        })
        .finally(() => {
          dispatchSearch(setLoading(false));
          // Reset firstTimeSearchRef after search completes to allow intersection observer to work
          firstTimeSearchRef.current = false;
        });
    } else if (pathname === "/products/orderstable") {
      customEventTrigger("pos_orderstable_search", {
        search_term: debouncedText,
      });
      dispatchSearch(orderFilter(debouncedText));
      dispatchSearch(setLoading(false));
    } else if (pathname === "/products/customertable") {
      /*
      const payload = {
        orgId: orgId,
        pageNo: 0,
        pageSize: 10,
        mobile: isNumberSearch ? debouncedText : null,
        name: !isNumberSearch ? debouncedText : null,
      };
      */
      const payload = {
        organizationId: orgId,
        search: debouncedText,
        pageNumber: 0,
        pageSize: 10,
      };

      customEventTrigger("pos_customer_table_search", {
        searchText: debouncedText,
      });

      /*
      customerSearch(payload)
      */
      customerSearchV2Api(payload)
        .then((res) => {
          if (res?.data?.customers) {
            const uidxArr = res?.data?.customers?.map((e) => e?.uidx || e?.id);
            dispatchSearch(
              customerFilter({ p: { uidx: uidxArr }, r: res })
            );
          } else {
            dispatchSearch(customerFilter(null));
          }
        })
        .catch((err) => {
          showSnackbar(err?.message, "error");
          dispatchSearch(setLoading(false));
        });

      dispatchSearch(setLoading(false));
    } else {
      dispatchSearch(setLoading(false));
    }
  }, [debouncedText, saleMode]);

  // ---------- INSTANT CLEAR ----------
  const handleClearSearch = () => {
    updateDebounced.cancel();
    // Set firstTimeSearchRef to true so that clearing triggers API call in useEffect
    firstTimeSearchRef.current = true;
    setSearchText("");
    setDebouncedText("");

    props?.setSelectedCategory?.("");
    props?.handleFilters?.("ALL", "");
    setCategoryName("");

    setTimeout(() => {
      document.getElementById("search-input")?.focus();
    }, 100);
  };

  return (
    <div className={`common-search-bar ${color === "dark" ? "dark" : ""}`}>
      <PosCustomIcon
        icon={<MagnifyingGlassIcon />}
        height={"20px"}
        width={"20px"}
        color="#535861"
      />

      <input
        tabIndex={-1}
        id="search-input"
        className="common-search-input"
        placeholder="Search for food items"
        onChange={(e) => setSearchText(e.target.value)}
        value={searchText}
        onKeyDown={(e) => {
          if (e.key === "Enter" && searchText.trim()) {
            updateDebounced.cancel();
            setDebouncedText(searchText);
          }
        }}
        onFocus={() => {
          setIsInputFocused(true);
          dispatchSearch(setFocus(true));
        }}
        onBlur={() => {
          setIsInputFocused(false);
          dispatchSearch(setFocus(false));
        }}
        autoComplete="off"
      />

      {searchText && (
        <PosCustomIcon
          icon={<XMarkIcon />}
          height={"20px"}
          width={"20px"}
          color="#535861"
          pointer={true}
          onClickFunction={handleClearSearch}
        />
      )}
    </div>
  );
};

export default CommonSearchBar;
